import { useState } from 'react';
import type { StoreProduct } from '../types';

interface Props {
  products: StoreProduct[];
  onAskAbout: (product: StoreProduct) => void;
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

function ProductTile({ product, onAskAbout }: { product: StoreProduct; onAskAbout: (p: StoreProduct) => void }) {
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <article className="group flex min-w-0 flex-col overflow-hidden rounded-[20px] border border-black/5 bg-white shadow-[0_2px_10px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(15,23,42,0.10)]">
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#eef0f3]">
        {product.imageUrl && !imageFailed ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.045]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-mid/30">
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <path d="M3 6h18M16 10a4 4 0 0 1-8 0" />
            </svg>
          </div>
        )}

        {product.category && (
          <span className="absolute left-3 top-3 max-w-[calc(100%-1.5rem)] truncate rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-primary shadow-sm backdrop-blur">
            {product.category}
          </span>
        )}

        {!product.inStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-primary/55 backdrop-blur-[1px]">
            <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-primary">Out of stock</span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="min-w-0 flex-1 text-sm font-semibold leading-snug text-primary sm:text-base">
            {product.name}
          </h3>
          <p className="shrink-0 text-sm font-bold text-primary sm:text-base">
            {formatPrice(product.price, product.currency)}
          </p>
        </div>

        {product.description && (
          <p className="mt-2 line-clamp-2 text-xs leading-5 text-mid sm:text-sm">
            {product.description}
          </p>
        )}

        <button
          onClick={() => onAskAbout(product)}
          disabled={!product.inStock}
          className="mt-4 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-full border border-border px-3 text-xs font-semibold text-primary transition-all hover:border-[var(--brand-accent)] hover:bg-[var(--brand-accent)] hover:text-white active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-primary sm:text-sm"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {product.inStock ? 'Ask about this' : 'Unavailable'}
        </button>
      </div>
    </article>
  );
}

export function ProductGrid({ products, onAskAbout }: Props) {
  if (products.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
      {products.map((product) => (
        <ProductTile key={product.id} product={product} onAskAbout={onAskAbout} />
      ))}
    </div>
  );
}

export default ProductGrid;
